/**
 * Customer detail page — anagrafica cliente + storico preventivi raggruppati
 * per progetto. Totali per status (vinti / aperti / persi) per snapshot rapido.
 */
import React, { useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import AppSidebar from '@/components/layout/AppSidebar';
import Header from '@/components/layout/Header';
import PageContainer from '@/components/layout/PageContainer';
import BackButton from '@/components/layout/BackButton';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Building, User, MapPin, Phone, Mail, FileText, Trophy, XCircle,
  Send, Euro, FolderOpen, ArrowRight, Hash, Plus, Target,
} from 'lucide-react';
import { useCustomer } from '@/hooks/useCustomers';
import { useCurrentOrganization } from '@/contexts/OrganizationContext';
import EstimateStatusBadge from '@/components/estimates/EstimateStatusBadge';
import CreateProjectDialog from '@/components/projects/CreateProjectDialog';
import { formatNumber } from '@/utils/format';

function euro(n: number): string {
  return `€ ${Math.round(n).toLocaleString('it-IT')}`;
}

const CustomerDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentOrganizationId } = useCurrentOrganization();
  const { data: customer, isLoading } = useCustomer(id);
  const [createOpen, setCreateOpen] = useState(false);

  // Carica progetti del cliente + relativi preventivi
  const { data: projectsWithEstimates = [] } = useQuery({
    queryKey: ['customer-projects-estimates', id, currentOrganizationId],
    enabled: !!id && !!currentOrganizationId,
    queryFn: async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: projs } = await (supabase as any)
        .from('projects')
        .select('id, name, status, created_at')
        .eq('customer_id', id)
        .eq('organization_id', currentOrganizationId);
      if (!projs || projs.length === 0) return [];
      const projectIds = projs.map((p: { id: string }) => p.id);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: ests } = await (supabase as any)
        .from('estimates')
        .select('id, name, status, total_amount, created_at, project_id, version, parent_estimate_id')
        .in('project_id', projectIds);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (projs as any[]).map((p: any) => ({
        ...p,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        estimates: ((ests as any[]) ?? []).filter(e => e.project_id === p.id),
      }));
    },
  });

  // Aggregati top-level
  const aggregates = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const allEstimates: any[] = projectsWithEstimates.flatMap(p => p.estimates ?? []);
    const wonValue = allEstimates
      .filter(e => e.status === 'won' || e.status === 'contracted')
      .reduce((s, e) => s + Number(e.total_amount ?? 0), 0);
    const openValue = allEstimates
      .filter(e => e.status === 'sent' || e.status === 'pending' || e.status === 'draft')
      .reduce((s, e) => s + Number(e.total_amount ?? 0), 0);
    const lostValue = allEstimates
      .filter(e => e.status === 'lost')
      .reduce((s, e) => s + Number(e.total_amount ?? 0), 0);
    const wonCount = allEstimates.filter(e => e.status === 'won' || e.status === 'contracted').length;
    const openCount = allEstimates.filter(e => e.status === 'sent' || e.status === 'pending' || e.status === 'draft').length;
    const lostCount = allEstimates.filter(e => e.status === 'lost').length;
    // Win rate: vinti / (vinti + persi). Esclude gli "aperti" che sono ancora in trattativa.
    const closed = wonCount + lostCount;
    const winRate = closed > 0 ? (wonCount / closed) * 100 : null;
    const avgWonTicket = wonCount > 0 ? wonValue / wonCount : 0;
    return {
      totalEstimates: allEstimates.length,
      totalProjects: projectsWithEstimates.length,
      wonCount, openCount, lostCount,
      wonValue, openValue, lostValue,
      winRate,
      avgWonTicket,
    };
  }, [projectsWithEstimates]);

  if (isLoading) {
    return (
      <SidebarProvider>
        <div className="min-h-screen flex w-full">
          <AppSidebar />
          <SidebarInset className="flex flex-col">
            <Header />
            <PageContainer>
              <div className="animate-pulse space-y-4">
                <div className="h-8 w-1/3 bg-muted rounded" />
                <div className="h-32 bg-muted rounded" />
              </div>
            </PageContainer>
          </SidebarInset>
        </div>
      </SidebarProvider>
    );
  }

  if (!customer) {
    return (
      <SidebarProvider>
        <div className="min-h-screen flex w-full">
          <AppSidebar />
          <SidebarInset className="flex flex-col">
            <Header />
            <PageContainer>
              <BackButton />
              <Card className="mt-4">
                <CardHeader>
                  <CardTitle>Cliente non trovato</CardTitle>
                  <CardDescription>
                    Il cliente cercato non esiste o non appartiene a questa organizzazione.
                  </CardDescription>
                </CardHeader>
              </Card>
            </PageContainer>
          </SidebarInset>
        </div>
      </SidebarProvider>
    );
  }

  const isCompany = customer.type === 'company';

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <AppSidebar />
        <SidebarInset className="flex flex-col">
          <Header />
          <PageContainer>
            <div className="mb-4">
              <BackButton />
            </div>

            {/* HEADER ANAGRAFICA */}
            <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
              <div>
                <div className="flex items-center gap-3 mb-1">
                  {isCompany ? (
                    <Building className="h-7 w-7 text-primary" />
                  ) : (
                    <User className="h-7 w-7 text-primary" />
                  )}
                  <h1 className="text-3xl font-bold tracking-tight">{customer.name}</h1>
                  <Badge variant={isCompany ? 'secondary' : 'outline'}>
                    {isCompany ? 'Azienda' : 'Privato'}
                  </Badge>
                </div>
                {customer.notes && (
                  <p className="text-sm text-muted-foreground mt-2 max-w-2xl">{customer.notes}</p>
                )}
              </div>
              <Button onClick={() => setCreateOpen(true)} className="gap-1.5">
                <Plus className="h-4 w-4" />
                Nuovo progetto per questo cliente
              </Button>
            </div>

            {/* KPI cliente */}
            <div className="grid gap-4 md:grid-cols-3 mb-6">
              <Card className="border-emerald-200/50 bg-gradient-to-br from-emerald-50/60 to-background">
                <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
                  <CardTitle className="text-sm font-medium">Fatturato vinto</CardTitle>
                  <Trophy className="h-4 w-4 text-emerald-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{euro(aggregates.wonValue)}</div>
                  <p className="text-xs text-muted-foreground">{aggregates.wonCount} preventivi confermati</p>
                </CardContent>
              </Card>
              <Card className="border-amber-200/50 bg-gradient-to-br from-amber-50/60 to-background">
                <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
                  <CardTitle className="text-sm font-medium">Pipeline aperta</CardTitle>
                  <Send className="h-4 w-4 text-amber-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{euro(aggregates.openValue)}</div>
                  <p className="text-xs text-muted-foreground">{aggregates.openCount} bozze/inviati</p>
                </CardContent>
              </Card>
              <Card className="border-red-200/50 bg-gradient-to-br from-red-50/60 to-background">
                <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
                  <CardTitle className="text-sm font-medium">Valore perso</CardTitle>
                  <XCircle className="h-4 w-4 text-red-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{euro(aggregates.lostValue)}</div>
                  <p className="text-xs text-muted-foreground">{aggregates.lostCount} preventivi non andati a buon fine</p>
                </CardContent>
              </Card>
            </div>

            {/* Win-rate widget: stacked bar vinti/aperti/persi + scheda margine cliente */}
            {aggregates.totalEstimates > 0 && (
              <Card className="mb-6">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Target className="h-4 w-4 text-primary" />
                    Performance commerciale con questo cliente
                  </CardTitle>
                  <CardDescription>
                    {aggregates.winRate !== null
                      ? <>Hit rate sui preventivi chiusi: <span className="font-semibold text-foreground">{formatNumber(aggregates.winRate, 0)}%</span> ({aggregates.wonCount} vinti su {aggregates.wonCount + aggregates.lostCount} chiusi)</>
                      : <>Nessun preventivo ancora chiuso (vinto o perso)</>
                    }
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Stacked bar count */}
                  <div>
                    <div className="text-xs text-muted-foreground mb-1.5">Distribuzione preventivi</div>
                    <div className="h-3 w-full rounded-full overflow-hidden flex bg-muted">
                      {aggregates.wonCount > 0 && (
                        <div
                          className="bg-emerald-500 h-full"
                          style={{ width: `${(aggregates.wonCount / aggregates.totalEstimates) * 100}%` }}
                          title={`Vinti: ${aggregates.wonCount}`}
                        />
                      )}
                      {aggregates.openCount > 0 && (
                        <div
                          className="bg-amber-400 h-full"
                          style={{ width: `${(aggregates.openCount / aggregates.totalEstimates) * 100}%` }}
                          title={`Aperti: ${aggregates.openCount}`}
                        />
                      )}
                      {aggregates.lostCount > 0 && (
                        <div
                          className="bg-red-500 h-full"
                          style={{ width: `${(aggregates.lostCount / aggregates.totalEstimates) * 100}%` }}
                          title={`Persi: ${aggregates.lostCount}`}
                        />
                      )}
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-xs flex-wrap">
                      <span className="inline-flex items-center gap-1">
                        <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
                        Vinti <span className="font-semibold tabular-nums">{aggregates.wonCount}</span>
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <span className="inline-block w-2 h-2 rounded-full bg-amber-400" />
                        Aperti <span className="font-semibold tabular-nums">{aggregates.openCount}</span>
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <span className="inline-block w-2 h-2 rounded-full bg-red-500" />
                        Persi <span className="font-semibold tabular-nums">{aggregates.lostCount}</span>
                      </span>
                    </div>
                  </div>

                  {/* Mini-KPI ticket medio + valore aggregato */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 pt-2 border-t">
                    <div>
                      <div className="text-xs text-muted-foreground">Ticket medio vinto</div>
                      <div className="text-base font-semibold tabular-nums">{euro(aggregates.avgWonTicket)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Valore complessivo gestito</div>
                      <div className="text-base font-semibold tabular-nums">
                        {euro(aggregates.wonValue + aggregates.openValue + aggregates.lostValue)}
                      </div>
                    </div>
                    {aggregates.winRate !== null && (
                      <div>
                        <div className="text-xs text-muted-foreground">Hit rate</div>
                        <div className={`text-base font-semibold tabular-nums ${
                          aggregates.winRate >= 50 ? 'text-emerald-600' : aggregates.winRate >= 25 ? 'text-amber-600' : 'text-red-600'
                        }`}>
                          {formatNumber(aggregates.winRate, 0)}%
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="grid gap-6 lg:grid-cols-3">
              {/* Anagrafica dettagli */}
              <Card className="lg:col-span-1">
                <CardHeader>
                  <CardTitle className="text-base">Anagrafica</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  {customer.vat_number && (
                    <Row icon={<Hash className="h-4 w-4" />} label="P.IVA" value={customer.vat_number} />
                  )}
                  {customer.fiscal_code && (
                    <Row icon={<Hash className="h-4 w-4" />} label="C.F." value={customer.fiscal_code} />
                  )}
                  {(customer.address_line || customer.city) && (
                    <Row
                      icon={<MapPin className="h-4 w-4" />}
                      label="Sede"
                      value={[
                        customer.address_line,
                        customer.zip_code,
                        customer.city,
                        customer.province ? `(${customer.province})` : null,
                      ].filter(Boolean).join(' ')}
                    />
                  )}
                  {customer.phone && (
                    <Row icon={<Phone className="h-4 w-4" />} label="Telefono" value={customer.phone} />
                  )}
                  {customer.email && (
                    <Row icon={<Mail className="h-4 w-4" />} label="Email" value={customer.email} />
                  )}
                  {customer.pec && (
                    <Row icon={<Mail className="h-4 w-4" />} label="PEC" value={customer.pec} />
                  )}
                  {customer.sdi_code && (
                    <Row icon={<Hash className="h-4 w-4" />} label="SDI" value={customer.sdi_code} />
                  )}
                </CardContent>
              </Card>

              {/* Storico preventivi raggruppati per progetto */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <FolderOpen className="h-5 w-5 text-primary" />
                    Storico preventivi
                  </CardTitle>
                  <CardDescription>
                    {aggregates.totalProjects} progetti · {aggregates.totalEstimates} preventivi totali
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {projectsWithEstimates.length === 0 ? (
                    <div className="text-center py-10 text-sm text-muted-foreground">
                      <FileText className="h-10 w-10 mx-auto mb-2 opacity-30" />
                      Nessun progetto collegato a questo cliente.
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {projectsWithEstimates.map(project => (
                        <div key={project.id} className="border rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <Link
                              to={`/projects/${project.id}`}
                              className="font-semibold hover:underline flex items-center gap-1.5"
                            >
                              {project.name}
                              <ArrowRight className="h-3.5 w-3.5" />
                            </Link>
                            <span className="text-xs text-muted-foreground">
                              {project.estimates?.length ?? 0} preventivi
                            </span>
                          </div>
                          {(project.estimates ?? []).length === 0 ? (
                            <p className="text-xs text-muted-foreground">Nessun preventivo</p>
                          ) : (
                            <div className="space-y-1">
                              {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                              {(project.estimates as any[])
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                .map((e: any) => (
                                  <button
                                    key={e.id}
                                    onClick={() => navigate(`/estimates/${e.id}/manage`)}
                                    className="w-full text-left flex items-center justify-between gap-2 py-1.5 px-2 rounded hover:bg-muted/50 transition-colors"
                                  >
                                    <div className="flex items-center gap-2 min-w-0">
                                      <EstimateStatusBadge status={e.status} size="sm" />
                                      <span className="text-sm truncate">{e.name}</span>
                                      {e.version > 1 && (
                                        <Badge variant="outline" className="text-[10px] py-0 h-4">v{e.version}</Badge>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                      <span className="text-xs text-muted-foreground">
                                        {new Date(e.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: '2-digit' })}
                                      </span>
                                      <span className="text-sm font-mono">
                                        {euro(Number(e.total_amount ?? 0))}
                                      </span>
                                    </div>
                                  </button>
                                ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </PageContainer>
        </SidebarInset>
      </div>

      {/* Dialog "Nuovo progetto" pre-compilato con customer_id */}
      <CreateProjectDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        initialCustomerId={id}
      />
    </SidebarProvider>
  );
};

const Row: React.FC<{ icon: React.ReactNode; label: string; value: string }> = ({ icon, label, value }) => (
  <div className="flex items-start gap-2">
    <div className="text-muted-foreground mt-0.5">{icon}</div>
    <div className="flex-1 min-w-0">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="font-medium break-words">{value}</div>
    </div>
  </div>
);

export default CustomerDetail;
